<template>
  <template v-if="!loadingSettings">
    <Card class="w-full">
      <template #title>System</template>
      <template #content>
        <div class="h-[70vh] overflow-scroll">
          <Fieldset legend="Appearance" class="w-full" toggleable>
            <div class="grid grid-cols-3 lg:grid-cols-5 auto-cols-max grid-flow-row gap-3">
              <ColorPickerGroup
                v-for="setting in getColorSettings()"
                :setting="setting"
                :label="getColorSettingDisplayText(setting.settingName)"
                @commit-color="(name: string, value: string | null) => updateColorSetting(name, value)"
              />
            </div>
          </Fieldset>
          <Fieldset legend="Web Portal" toggleable>
            <div class="grid grid-cols-3 lg:grid-cols-5 auto-cols-max grid-flow-row gap-3">
              <IconToggleButton
                label="Allow Online Approvals"
                icon="receipt"
                :initial-value="getBooleanValue(SystemSetting.ALLOW_ONLINE_APPROVALS)"
                @toggle="(value: boolean) => updateSetting(SystemSetting.ALLOW_ONLINE_APPROVALS, value.toString())"
              />
            </div>
          </Fieldset>
          <!-- <Fieldset legend="Actions" class="w-full" toggleable>
            <div class="grid grid-cols-5 auto-cols-max grid-flow-row gap-3">
              <IconToggleButton
                label="Auto send request for approval when draw completed"
                icon="check"
                :initial-value="getBooleanValue(SystemSetting.SEND_REQUEST_FOR_APPROVAL_ON_DRAW_COMPLETED)"
                @toggle="(value: boolean) => updateSetting(SystemSetting.SEND_REQUEST_FOR_APPROVAL_ON_DRAW_COMPLETED, value.toString())"
              />
            </div>
          </Fieldset> -->
          <Fieldset legend="Default Import Group" class="w-full" toggleable>
            <div class="flex flex-col gap-2">
              <label class="font-bold">User Group</label>
              <Select
                name="group"
                id="group"
                type="text"
                :options="userGroups"
                optionLabel="name"
                :showClear="false"
                class="h-10 w-min"
                placeholder="Select a user group"
                v-model="importGroup"
                @change="updateDefaultImportGroup"
              />
            </div>
          </Fieldset>
        </div>
      </template>
    </Card>
  </template>
  <template v-else>
    <div class="flex flex-col w-full mt-20">
      <ProgressSpinner style="width: 200px; height: 200px" class="fill-surface-0 dark:fill-surface-800 mx-auto" animationDuration=".5s" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { ColorSettings, getColorSettingDisplayText } from "@/enums/ColorSettings";
import { SystemSetting } from "@/enums/SystemSettings";
import { getSystemSettings, updateSetting } from "@/services/SettingsService";
import { getUserGroups } from "@/services/Utility";
import type { Code } from "@/types/Codes";
import type { Setting } from "@/types/Settings";

const importGroup = ref<Code>();
const userGroups = ref<Code[]>([]);
const systemSettings = ref<Setting[]>([]);
const loadingSettings = ref<boolean>(true);

onBeforeMount(async (): Promise<void> => {
  await getSystemSettings().then((result: Setting[]) => (systemSettings.value = result));
  await getUserGroups(false, true).then((codes: Code[]) => (userGroups.value = codes));

  const importGroupSetting: Setting = systemSettings.value.filter(
    (setting: Setting) => setting.settingName === SystemSetting.DEFAULT_IMPORT_GROUP
  )[0];

  const selectedGroup: string = importGroupSetting?.settingValue ?? "ALL";

  for (let code of userGroups.value) {
    if (code.name === selectedGroup) {
      importGroup.value = code;
    }
  }

  loadingSettings.value = false;
});

const isColorSetting = (setting: Setting): boolean => {
  for (const color of Object.values(ColorSettings)) {
    if (setting.settingName === color.toString()) return true;
  }
  return false;
};

const getColorSettings = (): Setting[] => {
  return systemSettings.value.filter((setting: Setting) => isColorSetting(setting));
};

const getSettingFromName = (name: string): Setting => {
  return systemSettings.value.filter((setting: Setting) => setting.settingName === name)[0];
};

const updateColorSetting = (name: string, value: string | null): void => {
  if (value) {
    let setting: Setting = getSettingFromName(name);
    setting.settingValue = value;
    updateSetting(setting.settingName, setting.settingValue);
  }
};

const getBooleanValue = (systemSetting: SystemSetting): boolean => {
  const setting = systemSettings.value.filter((setting) => setting.settingName === systemSetting.toString())[0];

  if (setting?.settingValue) {
    return setting.settingValue.toLowerCase() === "true";
  }
  return false;
};

const updateDefaultImportGroup = (event: any): void => {
  if (!importGroup.value) {
    warn("Warning", "Invalid user group selected");
    return;
  }

  updateSetting(SystemSetting.DEFAULT_IMPORT_GROUP, importGroup.value.name);
  systemSettings.value.filter((setting: Setting) => setting.settingName === SystemSetting.DEFAULT_IMPORT_GROUP)[0].settingValue =
    importGroup.value.name;
};
</script>
